import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import type { PlaybackClock } from "@/lib/playback-clock";
import { createProgressOutbox, type ProgressOutbox } from "@/lib/progress-outbox";
import { touchSessionActivity } from "@/lib/session-activity";
import {
  postLessonProgress,
  sendLessonProgressOnExit,
  type LessonProgressResult,
} from "@/services/api/player";

type LessonProgressTrackerProps = {
  lessonId: string;
  hasVideo: boolean;
  isTextLesson: boolean;
  /** The active player's clock — a <video> for local lessons, Bunny's iframe for hosted ones. */
  clock: PlaybackClock | null;
  /** LRN-10: the saved position the lesson opens at (sent with the "opened" ping). */
  initialPositionSeconds?: number;
  /** Called once the server has accepted the lesson as completed (LRN-21 outline refresh). */
  onCompleted?: () => void;
  /** LRN-10: every result the server accepted (keeps the outline's progress current). */
  onSaved?: (result: LessonProgressResult) => void;
};

/** LRN-10: position posts are at least this far apart in wall-clock time. */
const REPORT_EVERY_MS = 10_000;
/** LRN-1: playback marks the session active at most this often (well inside the idle limit, N16). */
const ACTIVITY_EVERY_MS = 30_000;

export function LessonProgressTracker({
  lessonId,
  hasVideo,
  isTextLesson,
  clock,
  initialPositionSeconds = 0,
  onCompleted,
  onSaved,
}: LessonProgressTrackerProps) {
  const t = useTranslations("player");
  const [failing, setFailing] = useState(false);
  const outboxRef = useRef<ProgressOutbox | null>(null);
  const lastSentAtRef = useRef(0);
  /** Where playback is now, and the last position the server was sent. */
  const positionRef = useRef<number | null>(null);
  const sentPositionRef = useRef<number | null>(null);
  const lastActivityRef = useRef(0);
  const initialPositionRef = useRef(initialPositionSeconds);
  const onCompletedRef = useRef(onCompleted);
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onCompletedRef.current = onCompleted;
    onSavedRef.current = onSaved;
    initialPositionRef.current = initialPositionSeconds;
  }, [onCompleted, onSaved, initialPositionSeconds]);

  // AUTH-6: one outbox per lesson — retries failed posts and marks completion only on success.
  useEffect(() => {
    let notified = false;
    const outbox = createProgressOutbox({
      send: async (payload) => {
        const result = await postLessonProgress(lessonId, payload);
        if (result) onSavedRef.current?.(result);
        return result;
      },
      onStateChange: (state) => {
        setFailing(state.failing);
        if (state.completed && !notified) {
          notified = true;
          onCompletedRef.current?.();
        }
      },
    });
    outboxRef.current = outbox;
    positionRef.current = null;
    setFailing(false);
    if (isTextLesson) {
      outbox.report({ completed: true });
    } else if (hasVideo) {
      // LRN-10: opening a video makes it the "last viewed" lesson, at the position it resumes
      // from. (Quiz/homework lessons get a row when passed / submitted, not merely by opening.)
      const start = initialPositionRef.current;
      outbox.report({ positionSeconds: start });
      sentPositionRef.current = start;
    }
    lastSentAtRef.current = Date.now();

    // LRN-10: the tab is closing (or the page is being frozen) — send where playback is now.
    const flushOnExit = () => {
      const position = positionRef.current;
      if (position === null || position === sentPositionRef.current) return;
      if (sendLessonProgressOnExit(lessonId, { positionSeconds: position })) {
        sentPositionRef.current = position;
      }
    };
    window.addEventListener("pagehide", flushOnExit);

    return () => {
      window.removeEventListener("pagehide", flushOnExit);
      flushOnExit();
      outbox.dispose();
      if (outboxRef.current === outbox) outboxRef.current = null;
    };
  }, [hasVideo, isTextLesson, lessonId]);

  useEffect(() => {
    if (!hasVideo || !clock) return;

    const markActive = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_EVERY_MS) return;
      lastActivityRef.current = now;
      touchSessionActivity();
    };

    return clock.subscribe({
      onTime: (current, durationSec) => {
        // Time only advances while the video plays (or jumps on a seek) — a cross-origin Bunny
        // iframe included.
        markActive();
        positionRef.current = current;
        const outbox = outboxRef.current;
        if (!outbox) return;
        // Wall-clock throttle: a seek back (or forward) is saved on the next tick like any other.
        const now = Date.now();
        if (now - lastSentAtRef.current < REPORT_EVERY_MS) return;
        if (current === sentPositionRef.current) return;
        lastSentAtRef.current = now;
        sentPositionRef.current = current;
        // LRN-3: a completed lesson stays complete; re-watching only moves the saved position.
        outbox.report(
          outbox.state().completed
            ? { positionSeconds: current }
            : {
                watchedSeconds: current,
                positionSeconds: current,
                ...(durationSec !== undefined ? { durationSec } : {}),
              },
        );
      },
      onEnded: (durationSec) => {
        markActive();
        positionRef.current = durationSec;
        sentPositionRef.current = durationSec;
        lastSentAtRef.current = Date.now();
        // The server saves a position this close to the end as 0 (next visit starts over).
        outboxRef.current?.report({
          completed: true,
          watchedSeconds: durationSec,
          positionSeconds: durationSec,
          durationSec,
        });
      },
    });
  }, [clock, hasVideo, lessonId]);

  if (!failing) return null;
  return (
    <p
      role="status"
      className="rounded-md border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text-secondary)]"
      data-testid="lesson-progress-not-saved"
    >
      {t("progressNotSaved")}
    </p>
  );
}
